import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowUpRight, ArrowDownLeft, Loader2, Download, Search, RefreshCw, ExternalLink, ChevronDown, ChevronRight, EyeOff, RotateCcw, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CointrackerTransactions() {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [expandedTx, setExpandedTx] = useState({});
  const [editingTx, setEditingTx] = useState(null);
  const [editData, setEditData] = useState({});
  const [reparsing, setReparsing] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    wallet_id: 'all',
    category: 'all',
    status: 'active',
    search: '',
  });

  useEffect(() => {
    fetchWallets();
    fetchTransactions();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [filters, pagination.page]);

  const fetchWallets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cointracker/wallets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWallets(response.data.wallets || []);
    } catch (error) {
      console.error('Failed to fetch wallets:', error);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });
      
      if (filters.wallet_id !== 'all') params.append('wallet_id', filters.wallet_id);
      if (filters.category !== 'all') params.append('category', filters.category);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(`${API_URL}/cointracker/transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTransactions(response.data.transactions || []);
      setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleIgnoreTransaction = async (txId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/cointracker/transactions/${txId}/ignore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transaction ignored');
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to ignore transaction');
    }
  };

  const handleRestoreTransaction = async (txId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/cointracker/transactions/${txId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transaction restored');
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to restore transaction');
    }
  };

  const startEditing = (tx) => {
    setEditingTx(tx);
    setEditData({
      type: tx.type || tx.category || 'other',
      notes: tx.notes || '',
    });
  };

  const saveEdit = async () => {
    if (!editingTx) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/cointracker/transactions/${editingTx.tx_id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transaction updated - Journal entry will be regenerated');
      setEditingTx(null);
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to update transaction');
    }
  };

  const handleReparse = async () => {
    const ok = window.confirm(
      'Re-read transactions from the blockchain?\n\n' +
      'Transactions whose entry is still under review are read again and their entry is ' +
      'rebuilt only if the corrected data differs.\n\n' +
      'Approved, synced and ignored entries are left untouched.'
    );
    if (!ok) return;

    setReparsing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/cointracker/transactions/reparse`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const {
        reparsed, changed, journal_entries_generated,
        gas_advances_ignored, internal_transfers_matched,
      } = response.data;
      const notes = [];
      if (gas_advances_ignored) notes.push(`${gas_advances_ignored} gas advance round trip(s) ignored`);
      if (internal_transfers_matched) notes.push(`${internal_transfers_matched} internal transfer(s) matched`);
      toast.success(
        `${reparsed} transactions re-read, ${changed} corrected, ` +
        `${journal_entries_generated} entries rebuilt` +
        (notes.length ? `, ${notes.join(', ')}` : '')
      );
      fetchTransactions();
    } catch (error) {
      console.error('Reparse failed:', error);
      toast.error('Failed to re-parse transactions');
    } finally {
      setReparsing(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cointracker/transactions/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transactions-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export downloaded');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export transactions');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAddress = (address) => {
    if (!address || address === 'unknown') return 'Unknown';
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'send': return 'text-red-400';
      case 'receive': return 'text-green-400';
      case 'swap': return 'text-blue-400';
      default: return 'text-zinc-400';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'send': return <ArrowUpRight className="w-4 h-4 text-red-400" />;
      case 'receive': return <ArrowDownLeft className="w-4 h-4 text-green-400" />;
      case 'swap': return <RefreshCw className="w-4 h-4 text-blue-400" />;
      default: return <ArrowUpRight className="w-4 h-4 text-zinc-400" />;
    }
  };

  const toggleExpand = (txId) => {
    setExpandedTx(prev => ({ ...prev, [txId]: !prev[txId] }));
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="space-y-6">
      {/* Header & Filters */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Transactions</h2>
          <p className="text-sm text-zinc-400 mt-1">{pagination.total} total transactions</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleReparse}
            disabled={reparsing}
            className="border-zinc-700"
            data-testid="reparse-transactions-btn"
            title="Re-read Solana transactions from the chain. Only entries still under review are rebuilt - approved, synced and ignored ones are left untouched."
          >
            {reparsing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Re-parse chain
          </Button>
          <Button
            variant="outline"
            onClick={handleExportCSV}
            className="border-zinc-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Search by hash, address..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white pl-10"
                />
              </div>
            </div>
            <Select
              value={filters.wallet_id}
              onValueChange={(value) => setFilters(prev => ({ ...prev, wallet_id: value }))}
            >
              <SelectTrigger className="w-48 bg-zinc-800 border-zinc-700 text-white">
                <SelectValue placeholder="All Wallets" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="all" className="text-white">All Wallets</SelectItem>
                {wallets.map((wallet) => (
                  <SelectItem key={wallet.wallet_id} value={wallet.wallet_id} className="text-white">
                    {wallet.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.category}
              onValueChange={(value) => setFilters(prev => ({ ...prev, category: value }))}
            >
              <SelectTrigger className="w-36 bg-zinc-800 border-zinc-700 text-white">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="all" className="text-white">All Types</SelectItem>
                <SelectItem value="send" className="text-white">Send</SelectItem>
                <SelectItem value="receive" className="text-white">Receive</SelectItem>
                <SelectItem value="swap" className="text-white">Swap</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
            >
              <SelectTrigger className="w-36 bg-zinc-800 border-zinc-700 text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="all" className="text-white">All</SelectItem>
                <SelectItem value="active" className="text-white">Active</SelectItem>
                <SelectItem value="ignored" className="text-white">Ignored</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Transactions List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
        </div>
      ) : transactions.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12">
            <div className="text-center">
              <ArrowUpRight className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No transactions yet</h3>
              <p className="text-zinc-400 text-sm">
                Transactions will appear here once you sync your wallets
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {transactions.map((tx) => {
            const isExpanded = expandedTx[tx.tx_id];
            const sent = tx.sent || [];
            const received = tx.received || [];
            const category = tx.category || tx.type || 'other';
            
            return (
              <Card key={tx.tx_id} className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
                <CardContent className="p-0">
                  {/* Main Row */}
                  <div 
                    className="flex items-center gap-4 p-4 cursor-pointer hover:bg-zinc-800/30"
                    onClick={() => toggleExpand(tx.tx_id)}
                  >
                    {/* Expand Icon */}
                    <div className="text-zinc-500">
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </div>
                    
                    {/* Category */}
                    <div className="flex items-center gap-2 w-24">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        category === 'send' ? 'bg-red-500/10' :
                        category === 'receive' ? 'bg-green-500/10' :
                        'bg-blue-500/10'
                      }`}>
                        {getCategoryIcon(category)}
                      </div>
                      <span className={`text-sm font-medium capitalize ${getCategoryColor(category)}`}>
                        {category}
                      </span>
                    </div>
                    
                    {/* Sent */}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-zinc-500 uppercase mb-1">Sent</p>
                      {sent.length > 0 ? (
                        <div className="space-y-0.5">
                          {sent.slice(0, 2).map((s, idx) => (
                            <p key={idx} className="text-sm text-red-400 truncate">
                              -{s.amount?.toFixed(4)} {s.asset}
                            </p>
                          ))}
                          {sent.length > 2 && (
                            <p className="text-xs text-zinc-500">+{sent.length - 2} more</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-zinc-600">-</p>
                      )}
                    </div>
                    
                    {/* Received / Counterparty */}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-zinc-500 uppercase mb-1">
                        {category === 'send' ? 'To' : 'Received'}
                      </p>
                      {category === 'send' && sent.length > 0 ? (
                        <div className="space-y-0.5">
                          {sent.slice(0, 2).map((s, idx) => (
                            <p key={idx} className="text-sm text-zinc-400 font-mono truncate">
                              {formatAddress(s.counterparty)}
                            </p>
                          ))}
                        </div>
                      ) : received.length > 0 ? (
                        <div className="space-y-0.5">
                          {received.slice(0, 2).map((r, idx) => (
                            <p key={idx} className="text-sm text-green-400 truncate">
                              +{r.amount?.toFixed(4)} {r.asset}
                            </p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-zinc-600">-</p>
                      )}
                    </div>
                    
                    {/* Fee */}
                    <div className="w-20 text-right">
                      <p className="text-xs text-zinc-500 uppercase mb-1">Fee</p>
                      <p className="text-sm text-zinc-400">{formatCurrency(tx.fee_usd)}</p>
                    </div>
                    
                    {/* Value */}
                    <div className="w-28 text-right">
                      <p className="text-xs text-zinc-500 uppercase mb-1">Value</p>
                      <p className="text-sm text-white font-medium">
                        {formatCurrency(tx.total_sent_usd || tx.value_usd || 0)}
                      </p>
                    </div>
                    
                    {/* Date */}
                    <div className="w-36 text-right">
                      <p className="text-xs text-zinc-500 uppercase mb-1">Date</p>
                      <p className="text-sm text-zinc-400">{formatDate(tx.timestamp)}</p>
                    </div>
                    
                    {/* Status Badge */}
                    <div className="w-24">
                      {tx.status === 'ignored' ? (
                        <span className="px-2 py-1 text-xs rounded bg-zinc-500/20 text-zinc-400 border border-zinc-500/30">
                          Ignored
                        </span>
                      ) : tx.status === 'needs_review' ? (
                        <span className="px-2 py-1 text-xs rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                          Review
                        </span>
                      ) : tx.journal_entry_id ? (
                        <span className="px-2 py-1 text-xs rounded bg-green-500/20 text-green-400 border border-green-500/30">
                          Booked
                        </span>
                      ) : null}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      {tx.status === 'ignored' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRestoreTransaction(tx.tx_id)}
                          className="h-8 w-8 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                          title="Restore"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEditing(tx)}
                            className="h-8 w-8 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleIgnoreTransaction(tx.tx_id)}
                            className="h-8 w-8 p-0 text-zinc-400 hover:text-zinc-300 hover:bg-zinc-500/10"
                            title="Ignore"
                          >
                            <EyeOff className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-zinc-800 bg-zinc-800/20 p-4">
                      <div className="grid grid-cols-2 gap-6">
                        {/* All Sent Transfers */}
                        {sent.length > 0 && (
                          <div>
                            <h4 className="text-xs text-zinc-500 uppercase mb-2">Sent ({sent.length})</h4>
                            <div className="space-y-2">
                              {sent.map((s, idx) => (
                                <div key={idx} className="flex justify-between items-center bg-zinc-800/50 p-2 rounded">
                                  <div>
                                    <p className="text-sm text-red-400">-{s.amount?.toFixed(6)} {s.asset}</p>
                                    <p className="text-xs text-zinc-500 font-mono">
                                      To: {formatAddress(s.counterparty)}
                                    </p>
                                  </div>
                                  <p className="text-sm text-zinc-400">{formatCurrency(s.value_usd)}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* All Received Transfers */}
                        {received.length > 0 && (
                          <div>
                            <h4 className="text-xs text-zinc-500 uppercase mb-2">Received ({received.length})</h4>
                            <div className="space-y-2">
                              {received.map((r, idx) => (
                                <div key={idx} className="flex justify-between items-center bg-zinc-800/50 p-2 rounded">
                                  <div>
                                    <p className="text-sm text-green-400">+{r.amount?.toFixed(6)} {r.asset}</p>
                                    <p className="text-xs text-zinc-500 font-mono">
                                      From: {formatAddress(r.counterparty)}
                                    </p>
                                  </div>
                                  <p className="text-sm text-zinc-400">{formatCurrency(r.value_usd)}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Transaction Info */}
                      <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-between">
                        <div className="flex items-center gap-4 text-xs text-zinc-500">
                          <span>Wallet: {tx.wallet_name}</span>
                          <span>Hash: <span className="font-mono">{tx.tx_hash?.slice(0, 16)}...</span></span>
                        </div>
                        <a
                          href={`https://solscan.io/tx/${tx.tx_hash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                        >
                          View on Solscan <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-zinc-500">
                Page {pagination.page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  className="border-zinc-700"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= totalPages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  className="border-zinc-700"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Transaction Dialog */}
      <Dialog open={!!editingTx} onOpenChange={() => setEditingTx(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Transaction</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Modify the transaction category or add notes.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Type</label>
              <Select
                value={editData.type}
                onValueChange={(v) => setEditData(prev => ({ ...prev, type: v }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="send" className="text-white">Send</SelectItem>
                  <SelectItem value="receive" className="text-white">Receive</SelectItem>
                  <SelectItem value="transfer" className="text-white">Transfer</SelectItem>
                  <SelectItem value="sell" className="text-white">Sell</SelectItem>
                  <SelectItem value="buy" className="text-white">Buy</SelectItem>
                  <SelectItem value="interest income" className="text-white">Interest Income</SelectItem>
                  <SelectItem value="swap" className="text-white">Swap</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1">Changing the type will regenerate the journal entry</p>
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Notes</label>
              <Input
                value={editData.notes}
                onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                placeholder="Add notes about this transaction..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingTx(null)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={saveEdit}
              className="bg-white text-black hover:bg-zinc-200"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
